import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Horizontal scroller that advertises itself: an always-visible slim scrollbar plus
// a fade and a nudge arrow on whichever side still has content off-screen.
const ScrollX = ({ className = "", wrapperClassName = "", children }) => {
    const viewport = useRef(null);
    const [more, setMore] = useState({ left: false, right: false });

    const sync = useCallback(() => {
        const el = viewport.current;
        if (!el) return;
        const remaining = el.scrollWidth - el.clientWidth - el.scrollLeft;
        setMore({ left: el.scrollLeft > 2, right: remaining > 2 });
    }, []);

    useEffect(() => {
        const el = viewport.current;
        if (!el) return;
        sync();
        // Content and viewport both change size (zoom, window resize, rows loading in).
        const observer = new ResizeObserver(sync);
        observer.observe(el);
        if (el.firstElementChild) observer.observe(el.firstElementChild);
        return () => observer.disconnect();
    }, [sync]);

    const nudge = (direction) => {
        const el = viewport.current;
        if (el) el.scrollBy({ left: direction * el.clientWidth * 0.75, behavior: "smooth" });
    };

    return (
        <div className={`relative ${wrapperClassName}`}>
            <div ref={viewport} onScroll={sync} className={`hscroll ${className}`}>
                {children}
            </div>
            {more.left && (
                <>
                    <div className="pointer-events-none absolute left-0 top-0 bottom-2 w-10 bg-gradient-to-r from-white to-transparent rounded-l-xl" />
                    <button
                        type="button"
                        onClick={() => nudge(-1)}
                        aria-label="Scroll left"
                        className="absolute left-1 top-2 bg-white border border-gray-200 rounded-full shadow-md p-1.5 text-gray-600 hover:text-primary hover:border-primary transition-colors"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                </>
            )}
            {more.right && (
                <>
                    <div className="pointer-events-none absolute right-0 top-0 bottom-2 w-10 bg-gradient-to-l from-white to-transparent rounded-r-xl" />
                    <button
                        type="button"
                        onClick={() => nudge(1)}
                        aria-label="Scroll right"
                        className="absolute right-1 top-2 bg-white border border-gray-200 rounded-full shadow-md p-1.5 text-gray-600 hover:text-primary hover:border-primary transition-colors"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </>
            )}
        </div>
    );
};

export default ScrollX;
